import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from "react";
import { useDataContext } from "../hub/data-context-provider.js";
import { OpenRouterModelPicker } from "./agent-v2/OpenRouterModelPicker.js";
import { JournalistTraceModal } from "./JournalistTraceModal.js";
import { PROMPT_CHANGED_EVENT, } from "./JournalistPromptsPanel.js";
import { PromptEditor } from "./PromptEditor.js";
const JOURNALIST_ROLES = [
    {
        id: "extraction",
        label: "Field reporter",
        cadence: "extraction · 15m cron",
        blurb: "Transcribes what happened in channels — decisions, action items, blockers, architectural choices, cross-channel refs. Runs every 15 minutes; bulk + frequent — pick something cheap.",
    },
    {
        id: "synthesis",
        label: "Chief editor",
        cadence: "digest + synthesis · hourly :17",
        blurb: "Runs two passes. Once a day, threads the accumulated 15-min field reports into a single narrative digest and deletes the raw entries. Hourly, promotes unpromoted digests into the semantic / procedural / identity layers with citations + wiki-links. Wants restraint and structure — pick a smart model.",
    },
    {
        id: "knowledge_query",
        label: "Reference desk",
        cadence: "briefings + queries · interactive",
        blurb: "Powers knowledge briefings (per-agent) and ad-hoc knowledge queries against the vault. Latency-sensitive — pick something fast and reasonably smart.",
    },
];
function readRoleFromBag(bag, role) {
    const slot = bag?.[role];
    if (!slot || typeof slot !== "object")
        return null;
    const cast = slot;
    const provider = typeof cast.provider === "string" ? cast.provider : null;
    const model_id = typeof cast.model_id === "string" ? cast.model_id : null;
    if (!provider || !model_id)
        return null;
    return { provider, model_id };
}
// One entry per trigger button. The `requiresRole` map points at the
// role whose model gates that pass — extraction needs the field reporter,
// digest + synthesis both need the chief editor.
const PASSES = [
    {
        id: "extraction",
        label: "Field reporter pass",
        hint: "Transcribe channel activity since the last extraction.",
        requiresRole: "extraction",
    },
    {
        id: "digest",
        label: "Daily digest",
        hint: "Thread accumulated 15-min entries into one narrative; delete the raw.",
        requiresRole: "synthesis",
    },
    {
        id: "synthesis",
        label: "Synthesis pass",
        hint: "Promote unpromoted digest entries into semantic / procedural / identity.",
        requiresRole: "synthesis",
    },
];
function emptyAdvancedForm() {
    return {
        provider: "openrouter",
        model_id: "",
        prompt: "",
        start: "",
        end: "",
        date: "",
        digest_slug: "",
    };
}
// Map a manual PassId to the prompt-template role the preview endpoint
// expects. Field reporter → extraction, daily digest → digest, synthesis
// → synthesis. Same names happen to align here, but kept explicit so
// future role renames stay obvious.
const PASS_PREVIEW_ROLE = {
    extraction: "extraction",
    digest: "digest",
    synthesis: "synthesis",
};
// Format a Date as a `datetime-local` input value (YYYY-MM-DDTHH:MM).
// Keeps the form readable in the operator's locale; the Run handler
// turns it back into ISO-8601 before posting.
function toLocalInputValue(d) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
// Format a Date as YYYY-MM-DD in UTC. Used for the digest pass date
// default (UTC calendar day per Rocky's KTK-288 contract).
function toUtcDateString(d) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
export function JournalistDialog({ workspaceId, onClose, inline = false }) {
    const ctx = useDataContext();
    const [status, setStatus] = useState(null);
    const [orKeyConfigured, setOrKeyConfigured] = useState(null);
    const [models, setModels] = useState({
        extraction: "",
        synthesis: "",
        knowledge_query: "",
    });
    // Track which pass is currently running and the most recent result for
    // each pass independently — a digest result shouldn't clobber the
    // extraction summary the operator might still be reading.
    const [runningPass, setRunningPass] = useState(null);
    const [passResults, setPassResults] = useState({});
    // KTK-287 — last cron-run result per pass type. Read-only display: cron
    // results don't have a Run button. Populated from the
    // `/journalist/last-results` fetch on mount; refreshed after manual
    // triggers (which don't write to cron blobs but a cron tick may have
    // landed in between).
    const [cronResults, setCronResults] = useState({});
    // KTK-283 — process-trace modal. Holds the pass id whose result the
    // operator is currently inspecting; null when the modal is closed.
    // KTK-287 — extended to also accept cron pass ids so the same modal
    // surfaces traces for the "Last scheduled run" panel.
    const [viewingTrace, setViewingTrace] = useState(null);
    // KTK-291 — Advanced-collapsible state per manual pass. `expanded`
    // toggles the form; `forms` holds the typed values per pass id.
    // Fetching the composed prompt lazily on first expand avoids a
    // round-trip on mount that's only useful for power users.
    const [advancedExpanded, setAdvancedExpanded] = useState({});
    const [advancedForms, setAdvancedForms] = useState({
        extraction: emptyAdvancedForm(),
        digest: emptyAdvancedForm(),
        synthesis: emptyAdvancedForm(),
    });
    const [advancedPromptLoading, setAdvancedPromptLoading] = useState({});
    // Last value we received from /journalist/prompts/preview per pass.
    // Used to tell whether the operator has manually edited the Advanced
    // prompt textarea: if `form.prompt === lastFetchedPromptRef[pass]`,
    // it's still the auto-fetched value and safe to overwrite when the
    // hub-stored templates change. Otherwise it's a custom override and
    // we leave it alone.
    const lastFetchedPromptRef = useRef({
        extraction: "",
        digest: "",
        synthesis: "",
    });
    const [savingModels, setSavingModels] = useState(false);
    const [savingEnabled, setSavingEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        let cancelled = false;
        // Fetch status + key + last-results in parallel. last-results is a
        // best-effort load — if the endpoint is unavailable or empty (older
        // backend, never-run workspace) the pass result rows just stay
        // empty and the trace modal still works for in-session triggers.
        Promise.all([
            ctx.hub.getJournalistStatus(workspaceId),
            ctx.hub.getOpenRouterApiKey(workspaceId),
            ctx.hub.getJournalistLastResults(workspaceId).catch(() => null),
        ])
            .then(([s, k, last]) => {
            if (cancelled)
                return;
            setStatus(s);
            setOrKeyConfigured(k.configured);
            setModels({
                extraction: readRoleFromBag(s.settings, "extraction")?.model_id ?? "",
                synthesis: readRoleFromBag(s.settings, "synthesis")?.model_id ?? "",
                knowledge_query: readRoleFromBag(s.settings, "knowledge_query")?.model_id ?? "",
            });
            if (last) {
                const next = {};
                if (last.field_reporter)
                    next.extraction = { kind: "extraction", result: last.field_reporter };
                if (last.digest)
                    next.digest = { kind: "digest", result: last.digest };
                if (last.synthesis)
                    next.synthesis = { kind: "synthesis", result: last.synthesis };
                setPassResults(next);
                const cron = {};
                if (last.cron_field_reporter)
                    cron.extraction = { kind: "extraction", result: last.cron_field_reporter };
                if (last.cron_digest)
                    cron.digest = { kind: "digest", result: last.cron_digest };
                if (last.cron_synthesis)
                    cron.synthesis = { kind: "synthesis", result: last.cron_synthesis };
                setCronResults(cron);
            }
        })
            .catch((e) => {
            if (!cancelled)
                setError(e instanceof Error ? e.message : String(e));
        })
            .finally(() => {
            if (!cancelled)
                setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, workspaceId]);
    const allModelsPicked = Boolean(models.extraction) &&
        Boolean(models.synthesis) &&
        Boolean(models.knowledge_query);
    const missingModels = JOURNALIST_ROLES.filter((r) => !models[r.id]);
    const missingLabels = missingModels.map((r) => r.label.toLowerCase());
    const enabled = status?.enabled ?? false;
    // Schedule toggle: gates only the cron, not the trigger buttons. Same
    // models-picked rule applies here — Akari wants the cron to never
    // start with a half-configured panel.
    const canEnable = allModelsPicked && !savingEnabled;
    const showOpenRouterKeyWarning = orKeyConfigured === false;
    function scheduleHint() {
        if (savingEnabled)
            return "saving…";
        if (enabled) {
            return status?.last_run
                ? `Running. Last cron at ${new Date(status.last_run).toLocaleString()}.`
                : "Running. Next cron tick will populate the vault.";
        }
        if (!allModelsPicked) {
            return `Pick a model for ${missingLabels.join(", ")} before enabling the schedule.`;
        }
        return "Off. Toggle to start the cron at the next :15 / :17 tick.";
    }
    async function persistRoleSelection(role, modelId) {
        const next = {
            [role]: { provider: "openrouter", model_id: modelId },
        };
        setSavingModels(true);
        setError(null);
        try {
            await ctx.hub.updateJournalistSettings(workspaceId, next);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setSavingModels(false);
        }
    }
    async function setRoleModel(role, modelId) {
        setModels((m) => ({ ...m, [role]: modelId }));
        if (modelId) {
            await persistRoleSelection(role, modelId);
        }
    }
    async function setEnabled(next) {
        if (next && !canEnable)
            return;
        setSavingEnabled(true);
        setError(null);
        try {
            await ctx.hub.updateJournalistSettings(workspaceId, { enabled: next });
            const fresh = await ctx.hub.getJournalistStatus(workspaceId);
            setStatus(fresh);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setSavingEnabled(false);
        }
    }
    async function runPass(pass) {
        setRunningPass(pass);
        setError(null);
        try {
            const useAdvanced = advancedExpanded[pass] === true;
            const form = advancedForms[pass];
            let result;
            if (pass === "extraction") {
                if (useAdvanced) {
                    result = {
                        kind: "extraction",
                        result: await ctx.hub.runJournalistPassExtraction(workspaceId, {
                            provider: form.provider,
                            model_id: form.model_id,
                            prompt: form.prompt,
                            start: localInputToIso(form.start),
                            end: localInputToIso(form.end),
                        }),
                    };
                }
                else {
                    result = {
                        kind: "extraction",
                        result: await ctx.hub.triggerJournalist(workspaceId),
                    };
                }
            }
            else if (pass === "digest") {
                if (useAdvanced) {
                    result = {
                        kind: "digest",
                        result: await ctx.hub.runJournalistPassDigest(workspaceId, {
                            provider: form.provider,
                            model_id: form.model_id,
                            prompt: form.prompt,
                            date: form.date,
                        }),
                    };
                }
                else {
                    result = {
                        kind: "digest",
                        result: await ctx.hub.triggerJournalistDigest(workspaceId),
                    };
                }
            }
            else {
                if (useAdvanced) {
                    result = {
                        kind: "synthesis",
                        result: await ctx.hub.runJournalistPassSynthesis(workspaceId, {
                            provider: form.provider,
                            model_id: form.model_id,
                            prompt: form.prompt,
                            digest_slug: form.digest_slug,
                        }),
                    };
                }
                else {
                    result = {
                        kind: "synthesis",
                        result: await ctx.hub.triggerJournalistSynthesis(workspaceId),
                    };
                }
            }
            setPassResults((prev) => ({ ...prev, [pass]: result }));
            // Refresh status after extraction since it advances last_run.
            if (pass === "extraction") {
                const fresh = await ctx.hub.getJournalistStatus(workspaceId);
                setStatus(fresh);
            }
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setRunningPass(null);
        }
    }
    // KTK-291 — first-time expand handler for Advanced sections. Pre-fills
    // the form with workspace defaults: provider hardcoded to openrouter,
    // model id from the role's slot, scope args from sensible "now" values,
    // prompt fetched via /journalist/prompts/preview lazily.
    async function toggleAdvanced(pass) {
        const wasOpen = advancedExpanded[pass] === true;
        setAdvancedExpanded((prev) => ({ ...prev, [pass]: !wasOpen }));
        if (wasOpen)
            return;
        // First open — populate defaults if the form is still empty.
        const form = advancedForms[pass];
        const fresh = { ...form };
        if (!fresh.model_id) {
            // Field reporter uses the extraction slot; digest + synthesis use
            // the synthesis slot (Chief Editor model).
            fresh.model_id =
                pass === "extraction" ? models.extraction : models.synthesis;
        }
        const now = new Date();
        if (pass === "extraction" && !fresh.start && !fresh.end) {
            const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
            fresh.start = toLocalInputValue(oneHourAgo);
            fresh.end = toLocalInputValue(now);
        }
        if (pass === "digest" && !fresh.date) {
            fresh.date = toUtcDateString(now);
        }
        if (!fresh.prompt) {
            setAdvancedPromptLoading((prev) => ({ ...prev, [pass]: true }));
            try {
                const role = PASS_PREVIEW_ROLE[pass];
                const preview = await ctx.hub.previewJournalistPrompt(workspaceId, role);
                fresh.prompt = preview.composed;
                lastFetchedPromptRef.current[pass] = preview.composed;
            }
            catch (e) {
                // Non-fatal: leave prompt blank, surface error so operator knows.
                setError(e instanceof Error ? e.message : String(e));
            }
            finally {
                setAdvancedPromptLoading((prev) => ({ ...prev, [pass]: false }));
            }
        }
        setAdvancedForms((prev) => ({ ...prev, [pass]: fresh }));
    }
    // Refresh the composed prompt in any expanded Advanced section when
    // a hub-stored template changes (event dispatched by the Prompts
    // panel after a save or reset). If the operator has manually edited
    // the textarea (its value diverges from the last fetched preview),
    // leave it alone — that's their explicit override. Collapsed passes
    // get their cached prompt invalidated so the next expand re-fetches.
    useEffect(() => {
        function onPromptChanged(e) {
            const detail = e.detail;
            if (!detail || detail.workspaceId !== workspaceId)
                return;
            const passes = ["extraction", "digest", "synthesis"];
            passes.forEach((pass) => {
                const form = advancedForms[pass];
                const lastFetched = lastFetchedPromptRef.current[pass];
                // Untouched check: if no prompt has been fetched yet, nothing
                // to refresh. If the user has edited away from the last fetch,
                // respect their override.
                if (!lastFetched)
                    return;
                if (form.prompt !== lastFetched)
                    return;
                const expanded = advancedExpanded[pass] === true;
                if (expanded) {
                    const role = PASS_PREVIEW_ROLE[pass];
                    setAdvancedPromptLoading((prev) => ({ ...prev, [pass]: true }));
                    ctx.hub
                        .previewJournalistPrompt(workspaceId, role)
                        .then((preview) => {
                        lastFetchedPromptRef.current[pass] = preview.composed;
                        setAdvancedForms((prev) => ({
                            ...prev,
                            [pass]: { ...prev[pass], prompt: preview.composed },
                        }));
                    })
                        .catch(() => { })
                        .finally(() => {
                        setAdvancedPromptLoading((prev) => ({ ...prev, [pass]: false }));
                    });
                }
                else {
                    lastFetchedPromptRef.current[pass] = "";
                    setAdvancedForms((prev) => ({
                        ...prev,
                        [pass]: { ...prev[pass], prompt: "" },
                    }));
                }
            });
        }
        window.addEventListener(PROMPT_CHANGED_EVENT, onPromptChanged);
        return () => window.removeEventListener(PROMPT_CHANGED_EVENT, onPromptChanged);
    }, [ctx.hub, workspaceId, advancedExpanded, advancedForms]);
    function setAdvancedField(pass, key, value) {
        setAdvancedForms((prev) => ({
            ...prev,
            [pass]: { ...prev[pass], [key]: value },
        }));
    }
    // Convert a `datetime-local` input value (treated as the operator's
    // local time) back into an ISO-8601 string suitable for the backend.
    function localInputToIso(value) {
        if (!value)
            return "";
        const d = new Date(value);
        if (Number.isNaN(d.getTime()))
            return value;
        return d.toISOString();
    }
    const body = (_jsxs(_Fragment, { children: [_jsxs("header", { className: "ws-sect__head", children: [_jsx("h2", { className: "ws-sect__h", children: "Journalists" }), _jsx("p", { className: "ws-sect__sub", children: "The workspace's chroniclers. A field reporter transcribes channel activity, a chief editor digests it daily and promotes the digests into the vault's higher layers, and a reference desk answers knowledge briefings + queries." })] }), loading ? (_jsx("div", { className: "ws-card", children: _jsx("span", { className: "ws-mono", children: "Loading\u2026" }) })) : (_jsxs("div", { className: "ws-card", children: [error && (_jsx("div", { style: {
                            color: "var(--error)",
                            fontSize: 12,
                            padding: "8px 10px",
                            border: "1px solid rgba(250,82,82,0.3)",
                            borderRadius: 6,
                        }, children: error })), _jsxs("div", { className: "ws-jhead", children: [_jsxs("label", { className: "ws-toggle ws-toggle--lg", children: [_jsx("input", { type: "checkbox", checked: enabled, disabled: !canEnable && !enabled, onChange: (e) => void setEnabled(e.target.checked) }), _jsx("span", { className: "ws-toggle__track", children: _jsx("span", { className: "ws-toggle__thumb" }) })] }), _jsxs("div", { className: "ws-jhead__main", children: [_jsx("div", { className: "ws-jhead__name", children: "Run on schedule" }), _jsx("div", { className: "ws-jhead__sub", children: scheduleHint() })] }), _jsx("span", { className: `ws-pill ${enabled ? "ws-pill--ok" : "ws-pill--muted"}`, children: savingEnabled ? "saving…" : enabled ? "scheduled" : "off" })] }), showOpenRouterKeyWarning && (_jsxs("div", { style: {
                            padding: "8px 12px",
                            border: "1px solid color-mix(in oklab, oklch(70% 0.14 70) 40%, var(--hairline))",
                            background: "color-mix(in oklab, oklch(78% 0.14 70) 14%, var(--paper))",
                            borderRadius: "var(--radius-sm)",
                            color: "oklch(40% 0.13 70)",
                            fontSize: 12,
                            fontFamily: "var(--serif)",
                            fontStyle: "italic",
                            lineHeight: 1.5,
                        }, children: ["Workspace OpenRouter key not configured \u2014 passes will fail when run. Set the key under", " ", _jsx("span", { className: "ws-mono", children: "Integrations \u2192 OpenRouter" }), " first."] })), _jsx("div", { className: "ws-jroles", children: JOURNALIST_ROLES.map((role) => {
                            const selected = models[role.id];
                            return (_jsxs("div", { className: "ws-jrole", children: [_jsx("div", { className: "ws-jrole__head", children: _jsxs("div", { className: "ws-jrole__title", children: [_jsx("span", { className: "ws-jrole__name", children: role.label }), _jsx("span", { className: "ws-jrole__tag", children: role.cadence })] }) }), _jsx("p", { className: "ws-jrole__blurb", children: role.blurb }), _jsx(OpenRouterModelPicker, { value: selected, onChange: (slug) => void setRoleModel(role.id, slug), placeholder: "Pick a model \u2014 selection required" })] }, role.id));
                        }) }), _jsxs("div", { className: "ws-subsect", children: [_jsx("h3", { className: "ws-subsect__h", children: "Trigger a pass" }), _jsx("p", { className: "ws-subsect__s", children: "Run any journalist pass on demand. These work whether the schedule toggle is on or off \u2014 they only require the corresponding role's model to be picked." }), _jsx("div", { style: { display: "grid", gap: 8, marginTop: 6 }, children: PASSES.map((pass) => {
                                    const slotPicked = Boolean(models[pass.requiresRole]);
                                    const requiredRole = JOURNALIST_ROLES.find((r) => r.id === pass.requiresRole);
                                    const disabled = !slotPicked || runningPass !== null;
                                    const isRunning = runningPass === pass.id;
                                    const result = passResults[pass.id];
                                    const isAdvanced = advancedExpanded[pass.id] === true;
                                    const form = advancedForms[pass.id];
                                    const promptLoading = advancedPromptLoading[pass.id] === true;
                                    return (_jsxs("div", { style: {
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 8,
                                            padding: "8px 10px",
                                            border: "1px solid var(--hairline-2)",
                                            borderRadius: "var(--radius-sm)",
                                            background: "var(--paper-2)",
                                        }, children: [_jsxs("div", { style: {
                                                    display: "grid",
                                                    gridTemplateColumns: "1fr auto",
                                                    gap: 10,
                                                    alignItems: "center",
                                                }, children: [_jsxs("div", { style: { minWidth: 0 }, children: [_jsx("div", { style: {
                                                                    fontSize: 13,
                                                                    fontWeight: 600,
                                                                    color: "var(--ink)",
                                                                }, children: pass.label }), _jsx("div", { style: {
                                                                    fontSize: 11.5,
                                                                    color: "var(--ink-3)",
                                                                    marginTop: 2,
                                                                    fontFamily: "var(--serif)",
                                                                    fontStyle: "italic",
                                                                }, children: !slotPicked
                                                                    ? `Pick a model for ${requiredRole?.label.toLowerCase()} before running.`
                                                                    : isAdvanced
                                                                        ? "Run uses the Advanced params below."
                                                                        : pass.hint }), result && (_jsxs("div", { style: {
                                                                    fontSize: 11.5,
                                                                    color: "var(--ink-2)",
                                                                    marginTop: 4,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 8,
                                                                    flexWrap: "wrap",
                                                                }, children: [_jsx("span", { children: renderPassResult(result) }), _jsx("button", { type: "button", onClick: () => setViewingTrace(pass.id), style: {
                                                                            font: "inherit",
                                                                            fontSize: 10.5,
                                                                            padding: "1px 8px",
                                                                            borderRadius: 999,
                                                                            border: "1px solid var(--hairline)",
                                                                            background: "var(--paper)",
                                                                            color: "var(--ink-2)",
                                                                            cursor: "pointer",
                                                                            fontFamily: "var(--mono)",
                                                                            textTransform: "uppercase",
                                                                            letterSpacing: "0.06em",
                                                                        }, title: "Open the verbose process trace", children: "View trace \u2192" })] }))] }), _jsx("button", { type: "button", className: "ws-btn ws-btn--primary", onClick: () => void runPass(pass.id), disabled: disabled, children: isRunning ? "Running…" : "Run" })] }), _jsx("button", { type: "button", onClick: () => void toggleAdvanced(pass.id), style: {
                                                    alignSelf: "flex-start",
                                                    font: "inherit",
                                                    fontSize: 11,
                                                    padding: "2px 8px",
                                                    background: "transparent",
                                                    border: "1px solid var(--hairline)",
                                                    borderRadius: 999,
                                                    color: "var(--ink-3)",
                                                    cursor: "pointer",
                                                    fontFamily: "var(--mono)",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.06em",
                                                }, children: isAdvanced ? "▼ Advanced" : "▶ Advanced" }), isAdvanced && (_jsx(AdvancedSection, { passId: pass.id, form: form, promptLoading: promptLoading, onChange: (k, v) => setAdvancedField(pass.id, k, v) }))] }, pass.id));
                                }) }), savingModels && (_jsx("div", { style: {
                                    marginTop: 6,
                                    fontSize: 11.5,
                                    color: "var(--ink-3)",
                                    fontFamily: "var(--serif)",
                                    fontStyle: "italic",
                                }, children: "Saving model selection\u2026" }))] }), _jsxs("div", { className: "ws-subsect", children: [_jsx("h3", { className: "ws-subsect__h", children: "Last scheduled run" }), _jsxs("p", { className: "ws-subsect__s", children: ["Most recent cron-driven pass per type. Read-only \u2014 the schedule fires at ", _jsx("span", { className: "ws-mono", children: ":17" }), " hourly when \"Run on schedule\" is on. Each row's \"View trace \u2192\" button opens the same process-trace modal as the manual triggers."] }), _jsx("div", { style: { display: "grid", gap: 8, marginTop: 6 }, children: PASSES.map((pass) => {
                                    const result = cronResults[pass.id];
                                    return (_jsx("div", { style: {
                                            display: "grid",
                                            gridTemplateColumns: "1fr auto",
                                            gap: 10,
                                            alignItems: "center",
                                            padding: "8px 10px",
                                            border: "1px solid var(--hairline-2)",
                                            borderRadius: "var(--radius-sm)",
                                            background: "var(--paper-2)",
                                        }, children: _jsxs("div", { style: { minWidth: 0 }, children: [_jsx("div", { style: {
                                                        fontSize: 13,
                                                        fontWeight: 600,
                                                        color: "var(--ink)",
                                                    }, children: pass.label }), result ? (_jsxs(_Fragment, { children: [_jsx("div", { style: {
                                                                fontSize: 11.5,
                                                                color: "var(--ink-3)",
                                                                marginTop: 2,
                                                                fontFamily: "var(--serif)",
                                                                fontStyle: "italic",
                                                            }, children: formatCronStamp(result) }), _jsxs("div", { style: {
                                                                fontSize: 11.5,
                                                                color: "var(--ink-2)",
                                                                marginTop: 4,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 8,
                                                                flexWrap: "wrap",
                                                            }, children: [_jsx("span", { children: renderPassResult(result) }), _jsx("button", { type: "button", onClick: () => setViewingTrace(`cron_${pass.id}`), style: {
                                                                        font: "inherit",
                                                                        fontSize: 10.5,
                                                                        padding: "1px 8px",
                                                                        borderRadius: 999,
                                                                        border: "1px solid var(--hairline)",
                                                                        background: "var(--paper)",
                                                                        color: "var(--ink-2)",
                                                                        cursor: "pointer",
                                                                        fontFamily: "var(--mono)",
                                                                        textTransform: "uppercase",
                                                                        letterSpacing: "0.06em",
                                                                    }, title: "Open the verbose process trace", children: "View trace \u2192" })] })] })) : (_jsx("div", { style: {
                                                        fontSize: 11.5,
                                                        color: "var(--ink-3)",
                                                        marginTop: 2,
                                                        fontFamily: "var(--serif)",
                                                        fontStyle: "italic",
                                                    }, children: "No scheduled run recorded yet." }))] }) }, `cron-${pass.id}`));
                                }) })] }), !inline && (_jsxs("div", { className: "ws-actions", children: [_jsx("span", { className: "ws-actions__hint" }), _jsx("button", { type: "button", className: "ws-btn", onClick: onClose, children: "Close" })] }))] }))] }));
    // The trace modal renders as a sibling so its backdrop stacks over the
    // settings page (and over the parent dialog backdrop in modal mode).
    // Look up the result by the pass id stored in `viewingTrace` and only
    // mount when both the id and the result are defined.
    const traceModal = (() => {
        if (!viewingTrace)
            return null;
        const isCron = viewingTrace.startsWith("cron_");
        const baseId = (isCron ? viewingTrace.slice("cron_".length) : viewingTrace);
        const result = (isCron ? cronResults : passResults)[baseId];
        if (!result)
            return null;
        const labelByPass = {
            extraction: "Field reporter",
            digest: "Daily digest",
            synthesis: "Synthesis",
        };
        const label = `${isCron ? "Scheduled · " : ""}${labelByPass[baseId]}`;
        let pass;
        if (result.kind === "extraction") {
            pass = { kind: "extraction", label, result: result.result };
        }
        else if (result.kind === "digest") {
            pass = { kind: "digest", label, result: result.result };
        }
        else {
            pass = { kind: "synthesis", label, result: result.result };
        }
        return _jsx(JournalistTraceModal, { pass: pass, onClose: () => setViewingTrace(null) });
    })();
    if (inline) {
        return (_jsxs(_Fragment, { children: [body, traceModal] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx("div", { style: modalBackdrop, children: _jsx("div", { onClick: (e) => e.stopPropagation(), style: {
                        ...modalPanel,
                        width: 640,
                        maxHeight: "85vh",
                        overflow: "auto",
                    }, children: body }) }), traceModal] }));
}
// Render a one-line summary of a pass result. Each pass has its own
// shape; the empty-input cases use the `message` field so we just echo
// it back verbatim. Extraction's empty case is the watermark-already-
// caught-up situation — backend will surface a structured `message`
// once KTK-283 lands; until then we synthesize the same line locally
// when `messages_processed` is zero.
function renderPassResult(r) {
    if (r.kind === "extraction") {
        const x = r.result;
        if (x.message)
            return x.message;
        if (x.messages_processed === 0) {
            return "Already up to date — no new messages since last run.";
        }
        return (_jsxs(_Fragment, { children: ["Processed", " ", _jsx("span", { className: "ws-mono", children: x.messages_processed }), " messages", x.channels_processed !== undefined && (_jsxs(_Fragment, { children: [" ", "across ", _jsx("span", { className: "ws-mono", children: x.channels_processed }), " ", "channels"] })), x.cross_channel_refs ? (_jsxs(_Fragment, { children: [", ", _jsx("span", { className: "ws-mono", children: x.cross_channel_refs }), " ", "cross-channel refs"] })) : null, "."] }));
    }
    if (r.kind === "digest") {
        const d = r.result;
        if (d.message)
            return d.message;
        return (_jsxs(_Fragment, { children: ["Wrote digest ", _jsx("span", { className: "ws-mono", children: d.digest_slug }), " from", " ", _jsx("span", { className: "ws-mono", children: d.source_raw_count }), " raw entries; deleted", " ", _jsx("span", { className: "ws-mono", children: d.deleted_raw }), "."] }));
    }
    const s = r.result;
    if (s.message)
        return s.message;
    const parts = [];
    const push = (count, noun) => {
        if (!count)
            return;
        parts.push(_jsxs("span", { children: [_jsx("span", { className: "ws-mono", children: count }), " ", noun] }, noun));
    };
    push(s.semantic_written, "semantic");
    push(s.procedural_written, "procedural");
    push(s.identity_written, "identity");
    push(s.linker_actions_applied, "linker actions");
    push(s.promoted_marked, "promoted");
    if (parts.length === 0)
        return "Pass complete — no changes.";
    return (_jsxs(_Fragment, { children: ["Wrote", " ", parts.map((p, i) => (_jsxs(React.Fragment, { children: [i > 0 && ", ", p] }, i))), "."] }));
}
// KTK-291 — per-pass advanced form. Only the inputs the active pass's
// `/passes/*` endpoint requires are rendered: extraction needs start +
// end; digest needs date; synthesis needs digest_slug. Provider, model
// id, and prompt are common across all three.
function AdvancedSection({ passId, form, promptLoading, onChange, }) {
    return (_jsxs("div", { style: {
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: "10px 12px",
            background: "var(--paper)",
            border: "1px solid var(--hairline-2)",
            borderRadius: "var(--radius-sm)",
        }, children: [_jsxs("div", { style: {
                    display: "grid",
                    gridTemplateColumns: "1fr 2fr",
                    gap: 8,
                    alignItems: "center",
                }, children: [_jsx("label", { style: advLabel, children: "Provider" }), _jsx("input", { type: "text", value: form.provider, onChange: (e) => onChange("provider", e.target.value), className: "ws-field__i" }), _jsx("label", { style: advLabel, children: "Model" }), _jsx("div", { children: _jsx(OpenRouterModelPicker, { value: form.model_id, onChange: (slug) => onChange("model_id", slug), variant: "compact" }) }), passId === "extraction" && (_jsxs(_Fragment, { children: [_jsx("label", { style: advLabel, children: "Start" }), _jsx("input", { type: "datetime-local", value: form.start, onChange: (e) => onChange("start", e.target.value), className: "ws-field__i" }), _jsx("label", { style: advLabel, children: "End" }), _jsx("input", { type: "datetime-local", value: form.end, onChange: (e) => onChange("end", e.target.value), className: "ws-field__i" })] })), passId === "digest" && (_jsxs(_Fragment, { children: [_jsx("label", { style: advLabel, children: "Date (UTC)" }), _jsx("input", { type: "date", value: form.date, onChange: (e) => onChange("date", e.target.value), className: "ws-field__i" })] })), passId === "synthesis" && (_jsxs(_Fragment, { children: [_jsx("label", { style: advLabel, children: "Digest slug" }), _jsx("input", { type: "text", value: form.digest_slug, onChange: (e) => onChange("digest_slug", e.target.value), className: "ws-field__i mono", placeholder: "e.g. 2026-05-04-digest" })] }))] }), _jsx("label", { style: { ...advLabel, alignSelf: "start" }, children: "Prompt" }), promptLoading ? (_jsx("div", { style: {
                    padding: "10px 12px",
                    color: "var(--ink-3)",
                    fontFamily: "var(--mono)",
                    fontSize: 12,
                }, children: "Loading composed prompt\u2026" })) : (_jsx(PromptEditor, { value: form.prompt, onChange: (next) => onChange("prompt", next), defaultLines: 20 }))] }));
}
const advLabel = {
    fontFamily: "var(--mono)",
    fontSize: 9.5,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "var(--ink-3)",
};
// Pull the most useful timestamp out of a stored result for the cron
// "last run" panel. `completed_at` is the canonical stamp the backend
// writes when persisting; older blobs may only have `triggered_at`.
function formatCronStamp(r) {
    const stamp = r.result.completed_at ??
        r.result.triggered_at;
    if (!stamp)
        return "—";
    try {
        return `Last ran ${new Date(stamp).toLocaleString()}`;
    }
    catch {
        return stamp;
    }
}
const modalBackdrop = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "grid",
    placeItems: "center",
    zIndex: 100,
};
const modalPanel = {
    background: "var(--paper)",
    border: "1px solid var(--hairline)",
    borderRadius: 10,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 12,
};
//# sourceMappingURL=JournalistDialog.js.map